import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Phone, Mail, MapPin, Clock, Star } from 'lucide-react';
import { services } from '../data/services';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut' },
  }),
};

export default function Home() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <>
      {/* ── Hero ── */}
      <section id="hero" className="hero-section" data-testid="hero-section" ref={heroRef}>
        <div className="hero-bg-counter">
          <img
            src="https://customer-assets.emergentagent.com/job_44f8d55e-47d0-4698-9779-c21737e71933/artifacts/khyiit7b_Startseite%20Hintergrund%20bitte%20als%20flow%2C%20also%20scrollt%20mit%20beim%20scrollen.jpg"
            alt=""
            className="hero-bg-counter-img"
            style={{ transform: `translateY(${scrollY * -0.35}px)` }}
          />
          <div className="hero-bg-overlay" />
        </div>

        <div className="hero-container">
          <motion.div className="hero-text-block" initial="hidden" animate="visible" variants={fadeUp}>
            <p className="hero-overline" data-testid="hero-overline">MitGefühl &ndash; Coaching & Beratung</p>
            <h1 className="hero-heading" data-testid="hero-heading">
              <span className="hero-heading-script">
                <span className="hero-initial">A</span>lles, was du brauchst, um zu wachsen, trägst du bereits in dir&nbsp;&ndash;
              </span>
              <br />
              <span className="hero-heading-light">ich helfe dir, es wiederzufinden.</span>
            </h1>
            <p className="hero-sub" data-testid="hero-sub">
              Du bist auf der Suche nach mehr innerer Klarheit, emotionaler Balance und einem
              authentischen Weg zu dir selbst? Dann bist du hier genau richtig.
            </p>
            <div className="hero-cta-row">
              <a href="#contact" className="btn-primary" data-testid="hero-cta-contact">
                <Mail strokeWidth={1.5} size={18} />
                Nachricht senden
              </a>
              <a href="#angebote" className="btn-outline-light" data-testid="hero-cta-angebote">
                Angebote entdecken
                <ArrowRight strokeWidth={1.5} size={18} />
              </a>
            </div>
            <div className="hero-trust-row">
              <span className="trust-badge"><Star strokeWidth={1.5} size={16} /> Zertifizierter Coach</span>
              <span className="trust-badge"><CheckCircle strokeWidth={1.5} size={16} /> Staatl. anerkannte Sozialpädagogin</span>
              <span className="trust-badge"><CheckCircle strokeWidth={1.5} size={16} /> Über 5 Jahre Erfahrung</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Angebote ── */}
      <section id="angebote" className="section section-warm" data-testid="services-section">
        <div className="section-container">
          <motion.div className="section-intro" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="overline">Angebote</p>
            <h2 className="section-heading">Nimm dir, was du brauchst</h2>
            <p className="section-sub">Professionelle Begleitung für alle Lebensbereiche &ndash; filigran, klar und von Herzen.</p>
          </motion.div>

          <div className="services-grid">
            {services.map((svc, i) => (
              <motion.div
                key={svc.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <Link to={`/angebot/${svc.slug}`} className="service-card-link" data-testid={`service-card-${svc.slug}`}>
                  <Card className="service-card">
                    <div className="service-card-img-wrapper">
                      <img src={svc.image} alt={svc.title} className="service-card-img" />
                      <div className="service-card-img-overlay" />
                    </div>
                    <CardContent className="service-card-body">
                      <p className="service-card-overline">{svc.subtitle}</p>
                      <h3 className="service-card-title">{svc.title}</h3>
                      <p className="service-card-desc">{svc.shortDescription}</p>
                      <span className="service-card-cta">
                        Mehr erfahren <ArrowRight strokeWidth={1.5} size={16} />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Über mich ── */}
      <section id="about" className="section section-light" data-testid="about-section">
        <div className="section-container">
          <div className="about-layout">
            <motion.div className="about-image-col" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <img
                src="https://customer-assets.emergentagent.com/job_44f8d55e-47d0-4698-9779-c21737e71933/artifacts/z17k6wtc_%C3%9Cber%20mich%2C%20Vanessa.jpg"
                alt="Vanessa Würzler"
                className="about-portrait"
                data-testid="about-portrait"
              />
            </motion.div>

            <motion.div className="about-text-col" initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
              <p className="overline">Über mich</p>
              <h2 className="section-heading">So schön, dass du da bist!</h2>
              <div className="about-story">
                <p>
                  Mein Name ist Vanessa. Ich bin zweifache Mama und seit über zehn Jahren glücklich verheiratet.
                  Menschen auf ihrem Weg zu begleiten und etwas Positives in ihrem Leben zu bewirken,
                  war schon immer ein Herzensanliegen von mir.
                </p>
                <p>
                  Nach meinem Abitur schien zunächst das Lehramtsstudium der richtige Weg für mich zu sein.
                  Ich studierte, wurde währenddessen zum ersten Mal Mutter und fand mich im teilweise mehr als
                  herausfordernden Alltag zwischen Windeln, Bücherstapeln und Unterrichtsentwürfen wieder.
                </p>
                <p>
                  Nach meinem erfolgreich abgeschlossenen Staatsexamen startete ich voller Hoffnungen und Ziele als Lehrkraft.
                  Doch die Realität zerschlug all meine Vorstellungen. So viele Erwartungen von außen, Glückwünsche,
                  freudige Gesichter und doch fühlte ich mich leer.
                </p>
                <p>
                  Wer war ich fernab von der Studentin, der zukünftigen Lehrerin, der Mutter und Ehefrau?
                  Ich hatte mich selbst verloren. Doch die dunkelste Phase meines Lebens wurde rückblickend mein Lichtbringer.
                  Ich fand nach und nach zu mir selbst, wollte mich wirksam fühlen, Menschen begleiten und ihnen ermöglichen, ebenfalls ihr Leuchten zu entdecken.
                </p>
                <p>
                  In der Sozialpädagogik fand ich ebendiese Wirksamkeit. Ich schloss ein weiteres Studium erfolgreich ab
                  und spezialisierte mich als zertifizierter Coach darauf, Menschen mit tiefer Empathie und Verstand
                  auf ihrem Weg der inneren Balance zu begleiten.
                </p>
              </div>
              <div className="about-signature">
                <p className="about-closing">
                  Ich freue mich darauf, deine Geschichte kennenzulernen und Hand in Hand dein Leuchten zu entfachen.
                </p>
                <p className="about-sign-off">Alles Liebe<br />Vanessa</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Kontakt ── */}
      <section id="contact" className="section section-warm" data-testid="contact-section">
        <div className="section-container">
          <div className="contact-layout">
            <motion.div className="contact-info-col" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <p className="overline">Kontakt</p>
              <h2 className="section-heading">Lass uns sprechen</h2>
              <p className="contact-intro">
                Hast du Fragen oder möchtest du einen Termin vereinbaren?
                Ich freue mich auf deine Nachricht!
              </p>
              <div className="contact-details">
                <div className="contact-detail-item" data-testid="contact-phone">
                  <Phone strokeWidth={1.5} size={20} />
                  <span>0171 2956132</span>
                </div>
                <div className="contact-detail-item" data-testid="contact-email">
                  <Mail strokeWidth={1.5} size={20} />
                  <span>vanessa@mitgefuehl.live</span>
                </div>
                <div className="contact-detail-item" data-testid="contact-location">
                  <MapPin strokeWidth={1.5} size={20} />
                  <span>Deutschland &ndash; Vor Ort, Online & beim Kunden</span>
                </div>
                <div className="contact-detail-item" data-testid="contact-hours">
                  <Clock strokeWidth={1.5} size={20} />
                  <span>Mo&ndash;Fr: 9:00&ndash;18:00 Uhr</span>
                </div>
              </div>
            </motion.div>

            <motion.div className="contact-form-col" initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
              <Card className="contact-form-card">
                <CardContent className="contact-form-inner">
                  <h3 className="contact-form-title">Nachricht senden</h3>
                  <form onSubmit={handleContactSubmit} data-testid="contact-form">
                    <div className="form-row">
                      <Input
                        placeholder="Name *"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        data-testid="contact-name-input"
                      />
                      <Input
                        type="tel"
                        placeholder="Telefon *"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder="E-Mail (optional)"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="form-full"
                      data-testid="contact-email-input"
                    />
                    <Textarea
                      placeholder="Deine Nachricht *"
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      data-testid="contact-message-input"
                    />
                    <Button type="submit" className="btn-submit" disabled={submitStatus === 'sending'} data-testid="contact-submit-btn">
                      {submitStatus === 'sending' ? 'Wird gesendet...' : 'Nachricht senden'}
                    </Button>
                    {submitStatus === 'success' && (
                      <p className="form-success" data-testid="contact-success-msg">
                        Vielen Dank! Ich melde mich bald bei dir.
                      </p>
                    )}
                    {submitStatus === 'error' && (
                      <p className="form-error" data-testid="contact-error-msg">
                        Es gab einen Fehler. Bitte versuche es erneut.
                      </p>
                    )}
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
